import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Shield } from "lucide-react";
import { Button } from "@/components/ui/button";

export const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { path: "/", label: "Home" },
    { path: "/about", label: "About" },
    { path: "/fact-sheet", label: "Fact Sheet" },
    { path: "/resources", label: "Resources" },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <nav
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-40 transition-all duration-300 ${
        isScrolled
          ? "bg-slate-950/95 backdrop-blur-md shadow-lg shadow-black/20"
          : "bg-transparent"
      }`}
    >
      {/* Tricolor bar */}
      <div className="tricolor-bar" />

      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link
            to="/"
            data-testid="nav-logo"
            className="flex items-center gap-3 group"
          >
            <div className="p-2 rounded-lg bg-emerald-600/20 border border-emerald-500/30 group-hover:bg-emerald-600/30 transition-colors">
              <Shield className="w-5 h-5 text-emerald-400" />
            </div>
            <span className="font-serif text-lg md:text-xl font-semibold text-slate-100">
              Our<span className="text-emerald-400">Neutrality</span>
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-link-${link.label.toLowerCase()}`}
                className={`nav-link text-sm font-medium ${
                  isActive(link.path) ? "active" : ""
                }`}
              >
                {link.label}
              </Link>
            ))}
            <Link to="/take-action">
              <Button
                data-testid="nav-take-action-btn"
                className="bg-emerald-600 hover:bg-emerald-500 text-white px-6 py-2 rounded-full font-semibold transition-all hover:-translate-y-0.5 shadow-[0_0_20px_-5px_rgba(22,163,74,0.4)] hover:shadow-[0_0_30px_-5px_rgba(22,163,74,0.6)]"
              >
                Take Action
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            data-testid="mobile-menu-btn"
            className="md:hidden p-2 text-slate-300 hover:text-white transition-colors"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? (
              <X className="w-6 h-6" />
            ) : (
              <Menu className="w-6 h-6" />
            )}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div
          data-testid="mobile-menu"
          className="md:hidden bg-slate-900/98 backdrop-blur-lg border-t border-slate-800"
        >
          <div className="px-6 py-6 space-y-4">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`mobile-nav-link-${link.label.toLowerCase()}`}
                className={`block py-2 text-base font-medium ${
                  isActive(link.path) ? "text-emerald-400" : "text-slate-300"
                }`}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {link.label}
              </Link>
            ))}
            <Link
              to="/take-action"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              <Button
                data-testid="mobile-take-action-btn"
                className="w-full mt-4 bg-emerald-600 hover:bg-emerald-500 text-white py-3 rounded-full font-semibold"
              >
                Take Action
              </Button>
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
